import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import JsonView from '@uiw/react-json-view';
import { Column } from 'primereact/column';
import { Flag, PlayCircleOutline, Visibility } from '@mui/icons-material';
import { Paginator } from 'primereact/paginator';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip
} from '@mui/material';
import { Rating } from 'primereact/rating';
import { toast } from 'react-toastify';
import { IEffectItemStore } from '../../../../types/effects/effectsStoreInterface';
import Strings from '../../../../i18n';
import { colors } from '../../../../constants/colors';
import { EFFECTS_STATUS } from '../../../../constants/effectsEnums';
import {
  useConfigurationEffectMutation,
  useGetEffectCommandMutation
} from '../../../../services/effects/effectsService';
import { useEffectDefaultInLocMutation } from '../../../../services/effects/effectsStoreService';
import './jsonView.css';

interface EffectStoreListTableProps {
  selectedEffects: IEffectItemStore[];
  data: IEffectItemStore[];
  setSelectedEffects: (value: IEffectItemStore[]) => void;
  paginator: {
    currentPage: number;
    pageSize: number;
  };
  setPaginator: (value: { currentPage: number; pageSize: number }) => void;
  totalEffects: number;
  withLocation?: boolean;
  reloadTable: () => void;
}
export const EffectStoreListTable = ({
  selectedEffects,
  data,
  setSelectedEffects,
  totalEffects,
  paginator,
  setPaginator,
  withLocation,
  reloadTable
}: EffectStoreListTableProps) => {
  const [openDialogVideo, setOpenDialogVideo] = useState<boolean>(false);
  const [checked, setChecked] = useState(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draftJson, setDraftJson] = useState<string>('');
  const handleCloseDialogVideo = () => {
    setOpenDialogVideo(false);
  };
  const [effectToDefaultLocation, { isSuccess }] =
    useEffectDefaultInLocMutation();
  const [editEffectCommand, { isSuccess: isSuccessEditConfig }] =
    useConfigurationEffectMutation();
  const [openDialogCommand, setOpenDialogCommand] = useState<boolean>(false);

  const [effectSelectedForCommand, setEffectSelectedForCommand] =
    useState<IEffectItemStore | null>(null);

  const handleCloseDialogCommand = () => {
    setIsEditing(false);
    setOpenDialogCommand(false);
    setEffectSelectedForCommand(null);
  };

  const [
    getEffectCommand,
    { data: effectCommand, isLoading: isLoadingEffectCommand }
  ] = useGetEffectCommandMutation();

  const [effect, setEffect] = useState<IEffectItemStore>(
    {} as IEffectItemStore
  );

  useEffect(() => {
    if (isSuccess) {
      reloadTable();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccessEditConfig) {
      toast.success(Strings.effects.editCommandSuccessfully);
      getEffectCommand(effectSelectedForCommand?.id || 0);
    }
  }, [isSuccessEditConfig]);

  const handleEffectInDefaultLocation = (rowData: IEffectItemStore) => {
    effectToDefaultLocation({
      effectId: rowData.id,
      enabled: !rowData.defaultInLocation
    });
  };

  const renderVideoButton = (rowData: IEffectItemStore) => {
    return (
      <Button
        data-testid="play-button"
        onClick={() => {
          setEffect(rowData);
          setOpenDialogVideo(true);
        }}
        color="info"
      >
        <PlayCircleOutline />
      </Button>
    );
  };
  const getRating = (rowData: IEffectItemStore) => {
    return (
      <Rating
        value={rowData.rating}
        cancel={false}
        readOnly
        style={{ fontSize: '14px' }}
      />
    );
  };
  const renderName = (rowData: IEffectItemStore) => {
    return (
      <div>
        {rowData.isHaven && (
          <Tooltip title={Strings.effects.havenEffect}>
            <span>
              <Flag sx={{ color: colors.blueTheme, fontSize: '14px' }} />{' '}
            </span>
          </Tooltip>
        )}
        {rowData.name}
      </div>
    );
  };

  const renderPublishingStatus = (rowData: IEffectItemStore) => {
    const statusClasses: { [key: number]: string } = {
      1: 'text-warning',
      2: 'text-success'
    };

    const statusClass = statusClasses[rowData.publishingStatus];

    if (rowData.publishingStatus === EFFECTS_STATUS.DENIED) {
      return (
        <span style={{ color: 'red' }}>{rowData.publishingStatusString}</span>
      );
    }

    return (
      <span className={statusClass}>{rowData.publishingStatusString}</span>
    );
  };

  const onPageChange = (event: any) => {
    setPaginator({
      currentPage: event.page + 1,
      pageSize: event.rows
    });
  };

  const footer = (
    <div className="row">
      <div className="col-12">
        <Paginator
          first={(paginator.currentPage - 1) * paginator.pageSize}
          rows={paginator.pageSize}
          totalRecords={totalEffects}
          rowsPerPageOptions={[50, 250, 500]}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );

  const setViewCommand = (effectSelected: IEffectItemStore) => {
    setEffectSelectedForCommand(effectSelected);
    getEffectCommand(effectSelected.id);
    setOpenDialogCommand(true);
  };

  const renderViewCommand = (rowData: IEffectItemStore) => {
    return (
      <Button onClick={() => setViewCommand(rowData)}>
        <Visibility />
      </Button>
    );
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const renderDefaultLocation = (rowData: IEffectItemStore) => {
    return (
      <Checkbox
        checked={rowData.defaultInLocation}
        onClick={() => {
          handleEffectInDefaultLocation(rowData);
        }}
        color="secondary"
      />
    );
  };

  const handleStartEdit = (): void => {
    setDraftJson(JSON.stringify(effectCommand.configuration, null, 2));

    setIsEditing(true);
  };

  const handleChangeDraft = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { value } = e.target;
    setDraftJson(value);

    JSON.parse(value);
  };

  const handleSave = (): void => {
    const parsed = JSON.parse(draftJson);
    editEffectCommand({
      effectId: effectSelectedForCommand?.id || 0,
      configuration: parsed
    });
    setIsEditing(false);
  };

  return (
    <>
      <DataTable
        scrollHeight="flex"
        selection={selectedEffects}
        selectionMode="multiple"
        metaKeySelection
        onSelectionChange={(e) => {
          setSelectedEffects(e.value);
        }}
        value={data}
        size="small"
        scrollable
        selectionPageOnly
        tableStyle={{ fontSize: '14px' }}
        footer={totalEffects > 250 ? footer : null}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        {!withLocation && (
          <Column
            field="publishingRequestDate"
            header="Request date"
            sortable
            body={(rowData: IEffectItemStore) =>
              rowData.publishingRequestDateInterval
            }
          />
        )}
        <Column field="name" header="Name" sortable body={renderName} />
        <Column field="user" header="Owner" sortable />
        <Column field="effectType" header="Type" sortable />
        {!withLocation && (
          <Column field="userUploads" header="User Uploads" sortable />
        )}
        <Column field="rating" header="Rating" sortable body={getRating} />
        <Column field="" header="Video" body={renderVideoButton} />
        {!withLocation && (
          <Column
            field="publishingStatusString"
            header="Status"
            body={renderPublishingStatus}
            sortable
          />
        )}
        <Column field="" header="View Command" body={renderViewCommand} />
        <Column field="" header="View Command" body={renderDefaultLocation} />
      </DataTable>

      <Dialog open={openDialogVideo} onClose={handleCloseDialogVideo}>
        <DialogTitle>{effect?.name}</DialogTitle>
        <DialogContent>
          <video style={{ maxWidth: '340px' }} controls autoPlay loop>
            <source src={effect?.videoUrl} type="video/mp4" />
            <track kind="captions" src="" label="English" default />
            {Strings.common.videoNotSupported}
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogVideo} color="info">
            {Strings.common.cancel}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDialogCommand}
        onClose={handleCloseDialogCommand}
        fullWidth
        maxWidth="md"
      >
        {effectSelectedForCommand && (
          <>
            <DialogTitle>
              {effectSelectedForCommand?.name ?? 'Detalle'}
            </DialogTitle>

            <DialogContent>
              {isLoadingEffectCommand ? (
                <div style={{ padding: 16 }}> {Strings.common.loading}…</div>
              ) : (
                <div>
                  {!isEditing ? (
                    <JsonView
                      data-testid="effect-command-json"
                      value={effectCommand}
                      className="json-wrap"
                      style={{
                        fontSize: '14px',
                        width: '100%',
                        height: 450,
                        overflowX: 'hidden',
                        overflowY: 'auto'
                      }}
                      collapsed={2}
                      shortenTextAfterLength={1000}
                      displayDataTypes={false}
                      displayObjectSize={false}
                      indentWidth={2}
                    />
                  ) : (
                    <TextField
                      value={draftJson}
                      onChange={handleChangeDraft}
                      multiline
                      minRows={16}
                      fullWidth
                      InputProps={{
                        sx: {
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          whiteSpace: 'pre'
                        }
                      }}
                    />
                  )}
                </div>
              )}
            </DialogContent>

            <DialogActions>
              {!isEditing ? (
                <>
                  <Button
                    onClick={handleStartEdit}
                    color="primary"
                    variant="contained"
                  >
                    {Strings.common.edit}
                  </Button>
                  <Button onClick={handleCloseDialogCommand} color="info">
                    {Strings.common.cancel}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    color="success"
                    variant="contained"
                  >
                    {Strings.common.save}
                  </Button>
                  <Button onClick={handleCloseDialogCommand} color="inherit">
                    {Strings.common.cancel}
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
EffectStoreListTable.defaultProps = {
  withLocation: false
};
